import { useSyncExternalStore } from "react";

/**
 * Whether the assistant panel is open.
 *
 * A module store rather than context, for the same reason the theme is: the
 * beacon, the panel and eventually the header all need to open it, and they
 * do not share a useful common ancestor. Threading a callback down from
 * Layout would make every page that never mentions the assistant re-render
 * when it opens.
 *
 * `openedBy` exists so focus can be returned to whatever launched the panel.
 * Restoring focus to a remembered element is the part of a dialog that is
 * always skipped and always noticed — closing a modal and finding your Tab
 * position back at the top of the document is the most common way a keyboard
 * user loses their place.
 */

let open = false;
let openedBy: HTMLElement | null = null;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(): void {
  for (const listener of listeners) listener();
}

export function openChat(trigger?: HTMLElement | null): void {
  if (open) return;
  openedBy = trigger ?? (document.activeElement as HTMLElement | null);
  open = true;
  emit();
}

/**
 * Set when the panel closes and cleared by whoever takes focus back.
 *
 * The obvious implementation — remember the element and re-focus it — fails
 * here, and silently. The beacon unmounts while the panel is open (two
 * glowing things in one corner is a collision), so by the time the panel
 * closes the remembered node is **detached**, and focusing a detached element
 * does nothing at all: focus falls to `<body>` and a keyboard user is
 * returned to the top of the document. The element cannot be held across the
 * gap, so a flag is held instead and the beacon claims focus when it remounts.
 */
let pendingFocus = false;

export function closeChat(): void {
  if (!open) return;
  open = false;

  // If the opener is still in the document — a header button, say — it can be
  // focused directly. The beacon is not, so it gets the flag.
  const target = openedBy;
  openedBy = null;
  if (target?.isConnected) {
    requestAnimationFrame(() => {
      target.focus();
    });
  } else {
    pendingFocus = true;
  }

  emit();
}

/** True once, for the element that should take focus back. */
export function consumePendingFocus(): boolean {
  if (!pendingFocus) return false;
  pendingFocus = false;
  return true;
}

export function useChatOpen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => open,
    () => false,
  );
}
